import { useState, useEffect } from 'react';
import { 
  Coins, 
  ArrowUpCircle, 
  ArrowDownCircle, 
  TrendingUp, 
  TrendingDown,
  Download,
  RefreshCw,
  History,
  Info
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { toast } from 'sonner';
import { 
  getMyCredits, 
  exportMyCredits, 
  syncInvoiceCredits,
  formatCreditAmount,
  getTransactionTypeLabel,
  getSourceLabel 
} from '../services/creditService';

export const CreditsPage = () => {
  const [creditData, setCreditData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const fetchCredits = async () => {
    setLoading(true);
    try {
      const data = await getMyCredits(page, pageSize);
      setCreditData(data);
    } catch (error) {
      console.error('Error fetching credits:', error);
      toast.error('Fehler beim Laden der Credits');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await syncInvoiceCredits();
      if (result.synced > 0) {
        toast.success(`${result.total_credits_added} Credits aus ${result.synced} Rechnungen synchronisiert`);
      } else if (result.skipped > 0) {
        toast.info('Alle Credits sind bereits synchronisiert');
      }
      // No toast for "no contact found" - just refresh
      await fetchCredits();
    } catch (error) {
      console.error('Sync error:', error);
      toast.error('Fehler beim Synchronisieren');
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    const initCredits = async () => {
      // Silent auto-sync (no toasts)
      try { await syncInvoiceCredits(); } catch { /* ignore */ }
      // Then load credits data
      setLoading(true);
      try {
        const data = await getMyCredits(page, pageSize);
        setCreditData(data);
      } catch { /* no toast on init */ }
      finally { setLoading(false); }
    };
    initCredits();
  }, [page]);

  const handleExport = async () => {
    try {
      const data = await exportMyCredits();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `credits-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Export erfolgreich heruntergeladen');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Fehler beim Export');
    }
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'credit':
      case 'refund':
        return <ArrowUpCircle className="w-4 h-4 text-green-500" />;
      case 'debit':
      case 'expiry':
        return <ArrowDownCircle className="w-4 h-4 text-red-500" />;
      case 'adjustment':
        return <RefreshCw className="w-4 h-4 text-blue-500" />;
      default:
        return <Coins className="w-4 h-4" />;
    }
  };

  const getAmountColor = (amount) => {
    if (amount > 0) return 'text-green-600';
    if (amount < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const totalPages = creditData ? Math.ceil(creditData.total_transactions / pageSize) : 1;

  return (
    <div className="space-y-6" data-testid="credits-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
            Meine Credits
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
            Übersicht über Ihr Guthaben und alle Transaktionen
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleSync}
            disabled={syncing || loading}
            data-testid="sync-credits-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Synchronisiere...' : 'Aktualisieren'}
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleExport}
            data-testid="export-credits-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        {/* Current Balance */}
        <Card data-testid="balance-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Aktuelles Guthaben</CardTitle>
            <Coins className="h-4 w-4" style={{ color: 'var(--color-primary)' }} />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" style={{ color: 'var(--color-primary)' }}>
              {creditData?.current_balance?.toFixed(2) || '0.00'}
            </div>
            <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
              Credits verfügbar
            </p>
          </CardContent>
        </Card>

        {/* Total Credited */}
        <Card data-testid="total-credited-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Gesamt Gutschriften</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              +{creditData?.transactions?.filter(t => t.amount > 0).reduce((sum, t) => sum + t.amount, 0).toFixed(2) || '0.00'}
            </div>
            <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
              Insgesamt erhalten
            </p>
          </CardContent>
        </Card>

        {/* Total Debited */}
        <Card data-testid="total-debited-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Gesamt Verbrauch</CardTitle>
            <TrendingDown className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {creditData?.transactions?.filter(t => t.amount < 0).reduce((sum, t) => sum + t.amount, 0).toFixed(2) || '0.00'}
            </div>
            <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
              Insgesamt verbraucht
            </p>
          </CardContent>
        </Card>

        {/* Transaction Count */}
        <Card data-testid="transaction-count-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Transaktionen</CardTitle>
            <History className="h-4 w-4" style={{ color: 'var(--color-text-secondary)' }} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {creditData?.total_transactions || 0}
            </div>
            <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
              Gesamte Buchungen
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Transaction History */}
      <Card data-testid="transaction-history-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Transaktionsverlauf
          </CardTitle>
          <CardDescription>
            Alle Gutschriften und Abbuchungen in chronologischer Reihenfolge
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="w-8 h-8 animate-spin" style={{ color: 'var(--color-primary)' }} />
            </div>
          ) : creditData?.transactions?.length === 0 ? (
            <div className="text-center py-8" style={{ color: 'var(--color-text-secondary)' }}>
              <Coins className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Keine Transaktionen vorhanden</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12"></TableHead>
                    <TableHead>Datum</TableHead>
                    <TableHead>Typ</TableHead>
                    <TableHead>Beschreibung</TableHead>
                    <TableHead>Quelle</TableHead>
                    <TableHead className="text-right">Betrag</TableHead>
                    <TableHead className="text-right">Saldo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {creditData?.transactions?.map((tx) => (
                    <TableRow key={tx.ledger_id} data-testid={`transaction-row-${tx.ledger_id}`}>
                      <TableCell>
                        {getTransactionIcon(tx.transaction_type)}
                      </TableCell>
                      <TableCell className="font-medium">
                        {new Date(tx.created_at).toLocaleDateString('de-DE', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </TableCell>
                      <TableCell>
                        <Badge variant={tx.amount >= 0 ? 'default' : 'secondary'}>
                          {getTransactionTypeLabel(tx.transaction_type)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger className="flex items-center gap-1">
                              <span className="truncate max-w-[200px]">{tx.reason}</span>
                              {tx.description && <Info className="w-3 h-3" style={{ color: 'var(--color-text-secondary)' }} />}
                            </TooltipTrigger>
                            {tx.description && (
                              <TooltipContent>
                                <p>{tx.description}</p>
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                          {getSourceLabel(tx.source)}
                        </span>
                        {tx.source_reference && (
                          <span className="block text-xs opacity-60">
                            {tx.source_reference}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className={`text-right font-mono font-bold ${getAmountColor(tx.amount)}`}>
                        {formatCreditAmount(tx.amount)}
                      </TableCell>
                      <TableCell className="text-right font-mono" style={{ color: 'var(--color-text-secondary)' }}>
                        {tx.balance_after?.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-between items-center mt-4">
                  <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                    Seite {page} von {totalPages} ({creditData?.total_transactions} Einträge)
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      data-testid="prev-page-btn"
                    >
                      Zurück
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      data-testid="next-page-btn"
                    >
                      Weiter
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CreditsPage;
